import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import * as moment from 'moment';
import { DateService } from '@root/shared/service/date.service';

interface IHttpResponse {
  error_code: number;
  error_msg: string;
  data: any;
}
interface IGetHouseGroupList {
  format?: string;
}

interface IThirdpartyAccountList {
  thirdparty_types?: any;
}
interface IReservationOrderList {
  page: number;
  page_size: number;
  out_status?: string;
  house_ids?: any;
  house_names?: string;
  check_in?: string;
  check_out?: string;
  thirdparty_account_ids?: any;
  tag_ids?: any;
  exclude_tag_ids?: any;
  settlement_status?: number;
  channels?: any;
  contain_box?: number;
  keyword?: string;
  keyword_type?: string;
  start_date?: string;
  end_date?: string;
  date_type?: string;
  thirdparty_customer_id?: string;
  client_id?: string;
  export?: number;
  format?: string | 'split_by_reservation';
  hour_reservation?: number;
  reservation_box?: number;
}

interface IDecideOrder {
  reservation_code: string;
}

interface IHandleStayingStatus {
  reservation_code?: string;
  reservation_order_code?: string;
  to_status?: string;
}

interface IOperateReservationTag {
  reservation_code: string;
  operator_tag_id: number;
}

interface ITodayReservation {
  page: number;
  page_size: number;
  version: number;
}

interface IBoxList {
  page: number;
  page_size: number;
  type?: string;
  version: string | '1';
}

interface IGetReservationOrderList {
  code: string;
}

interface IForbiddenDateList {
  reservation_code: string;
}

interface IModifyBasicInfo {
  reservation_code: string;
  channel?: number;
  check_in?: string;
  check_out?: string;
  name?: string;
  mobile?: string;
  email?: string;
  remark?: string;
  number_of_guests?: number;
}

interface IModifyReservationAmount {
  reservation_code: string;
  house_fee?: number;
  extension_house_fee?: number;
  commission?: number;
  currency_type: string;
}

interface ISetCheckinTime {
  reservation_code: string;
  check_in_time?: string;
  check_out_time?: string;
}

interface IAddANote {
  reservation_code: string;
  note: string;
}

interface IRefreshOrder {
  reservation_order_code: string;
}

interface IPutReservationBox {
  reservation_code: string;
}

interface IOrderDetail {
  reservation_code: string;
  format: string;
  thirdparty_type?: number;
}

interface IIgnoreReservation {
  reservation_code: string;
  is_ignored: number;
}

interface IDeleteTransaction {
  transaction_id: number;
}

interface IGuestInfo {
  reservation_code: string;
  users: any;
}

interface IChangeHouseList {
  page: number;
  page_size: number;
  reservation_code: string;
  house_name: string;
}

interface IChangeToHouse {
  reservation_code: string;
  house_id: number;
}

interface IExpand {
  reservation_code?: string;
  reservation_order_code?: string;
  number: number;
  amount: number;
  payment_method?: number;
  paid_amount?: number;
}

interface IAirbnbFailedDetail {
  origin_house_id: string;
}

interface IAirbnbRetry {
  origin_house_id: string;
}

interface HouseListParamsInterface {
  page: number;
  page_size: number;
  title?: string;
  group_id?: number;
  house_type_id?: number;
  operator_relation_id?: number;
}

interface ISetGuideInterface {
  reservation_code: string;
  type: number;
  password?: string;
  visible_password_time?: string;
  need_credential?: number;
}

interface IGetShareInfo {
  reservation_code: string; // 入住单号
}

interface ISendGuideEmail {
  reservation_code: string;
  email: string;
}

interface IEditGuideAuditingStatus {
  reservation_code: string;
  status: number;
}

interface IRelateCredentialToGuest {
  client_id: number;
  guide_auditing_image_id: number;
}

interface IAddGuideImage {
  reservation_code: string;
  image: string;
}

const GET_HOUSE_GROUP_LIST_API = '/api/reservation/house_group_list';
const GET_THIRDPARTY_LIST_API = '/api/reservation/thirdparty_list';
const GET_RESERVATION_ORDER_LIST_API = '/api/reservation_order/list';
const GET_WAIT_ACCEPT_COUNT_API = '/api/reservation_order/wait_accept_count';
const APPROVE_ORDER_API = '/api/reservation/approve';
const DECLINE_ORDER_API = '/api/reservation/decline';
const HANDLE_STAYING_STATUS_API = '/api/reservation/handle_staying_status';
const REMOVE_RESERVATION_TAG_API = '/api/reservation/remove_tag';
const TODAY_RESERVATION_API = '/api/reservation/today_reservation';
const BOX_LIST_API = '/api/reservation/reservation_box_list';
const ADD_RESERVATION_TAG_API = '/api/reservation/add_tag';
const GET_RESERVATIONS_API = '/api/reservation_order/reservations';
const GET_CURRENCY_API = '/api/transaction/get_currency';
const FORBIDDEN_DATE_LIST_API = '/api/reservation/forbidden_date_list';
const MODIFY_BASIC_INFO_API = '/api/reservation/modify_basic_info';
const MODIFY_RESERVATION_AMOUNT_API =
  '/api/reservation/modify_reservation_amount';
const SET_CHECKIN_TIME_API = '/api/reservation/set_checkin_time';
const ADD_A_NOTE_API = '/api/reservation/add_note';
const REFRESH_RESERVATION_ORDER_API = '/api/reservation_order/refresh';
const PUT_RESERVATION_BOX_API = '/api/reservation/put_reservation_box';
const DELETE_ORDER_API = '/api/reservation_calendar/delete_handmade_order';
const GET_SETTINGS_API = '/api/house_status_setting/get';
const ORDER_DETAIL_API = '/api/reservation/detail';
const BOX_LIST_IGNORE_API = '/api/reservation/ignore';
const DELETE_TRANSACTION_API = '/api/reservation/delete_transaction';
const UPDATE_GUEST_INFO_API = '/api/reservation/update_guest_info';
const CHANGE_HOUSE_LIST_API = '/api/reservation/change_house_list';
const CHANGE_TO_HOUSE_API = '/api/reservation/change';
const EXPAND_API = '/api/reservation/expand';
const AIRBNB_FAILED_DETAIL_API = '/api/airbnb_link/failed_detail';
const AIRBNB_RETRY_API = '/api/airbnb_link/retry';
const AIRBNB_FAILED_LIST_API = '/api/airbnb_link/failed_list';
const BOOK_BY_HAND_API = '/api/reservation/book_by_hand';
const SET_GUIDE_API = '/api/reservation/set_guide';
const GET_SHARE_URL_API = '/api/reservation/share_url';
const SEND_GUIDE_EMAIL_API = '/api/reservation/send_guide_email';
const EDIT_GUIDE_AUDITING_STATUS_API =
  '/api/reservation/edit_guide_auditing_status';
const RELATE_CREDENTIAL_TO_GUEST_API = '/api/reservation/relate_client';

const HOUSE_LIST_API = '/api/house/search';

const ADD_GUIDE_IMAGE_API = '/api/reservation/add_guide_image';
const BATCH_PUT_BOX_API = '/api/reservation/batch_put_reservation_box'
const LOCK_ORDER_API = '/api/reservation/sync_lock';
const SET_AUTOMATION_API = '/api/reservation/set_automation';
const BATCH_CHANGE_HOUSE_API = '/api/reservation/batch_change';
const SUBACCOUNT_LIST_API = '/api/sub_account/list';
const SET_CREATOR_API = '/api/reservation/set_creator';
@Injectable({
  providedIn: 'root',
})
export class OrderV2Service {
  orderListRefresh$ = new Subject<boolean>();
  selectAllReservation$ = new Subject<boolean>();

  private tableLoadingChange = new EventEmitter();
  get tableLoadingOrNot() {
    if (this.tableLoadingChange === null) {
      this.tableLoadingChange = new EventEmitter();
    }
    return this.tableLoadingChange;
  }

  // 用于监控订单标签的动态更改
  public refreshOrderDetail = new EventEmitter();
  get refreshOrderDetailOrNot() {
    if (this.refreshOrderDetail === null) {
      this.refreshOrderDetail = new EventEmitter();
    }
    return this.refreshOrderDetail;
  }

  // 用于订单详情获取动态配置
  public settingOptions = new EventEmitter();
  public gethouseStatusSettingOptions() {
    if (this.settingOptions === null) {
      this.settingOptions = new EventEmitter();
    }
    return this.settingOptions;
  }

  constructor(private http: HttpClient, private dateService: DateService) {}

  updateOrderListRefresh(refresh) {
    this.orderListRefresh$.next(refresh);
  }

  getLockPasswordVisibleTimeList() {
    const options = [];
    for (let i = 0; i < 24; i++) {
      const hour = moment().startOf('day').add(i, 'hours').toDate();
      options.push({
        label: this.dateService.format(hour, [null, 'hm']),
        value: i < 10 ? `0${i}:00` : `${i}:00`,
      });
    }
    return options;
  }

  /** 订单查询房源分组和房源列表 */
  getHouseGroupList(params: IGetHouseGroupList) {
    const query = new HttpParams().append('format', `${params.format}`);
    return this.http.get(GET_HOUSE_GROUP_LIST_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 获得渠道账号列表 */
  getThirdpartyAccountList(params: IThirdpartyAccountList) {
    const query = new HttpParams().append(
      'thirdparty_types',
      `${params.thirdparty_types}`
    );
    return this.http.get(GET_THIRDPARTY_LIST_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 预订单列表  V2.7.2开始使用*/
  getReservationOrderList(params) {
    // const query = new HttpParams()
    //   .append("page", `${params.page}`)
    //   .append("page_size", `${params.page_size}`)
    //   .append("out_status", `${params.out_status}`)
    //   .append("house_ids", `${params.house_ids}`)
    //   .append("house_names", `${params.house_names}`)
    //   .append("check_in", `${params.check_in}`)
    //   .append("check_out", `${params.check_out}`)
    //   .append("thirdparty_account_ids", `${params.thirdparty_account_ids}`)
    //   .append("tag_ids", `${params.tag_ids}`)
    //   .append("exclude_tag_ids", `${params.exclude_tag_ids}`)
    //   .append("settlement_status", `${params.settlement_status}`)
    //   .append("channels", `${params.channels}`)
    //   .append("contain_box", `${params.contain_box}`)
    //   .append("keyword", `${params.keyword}`)
    //   .append("keyword_type", `${params.keyword_type}`)
    //   .append("start_date", `${params.start_date}`)
    //   .append("end_date", `${params.end_date}`)
    //   .append("date_type", `${params.date_type}`)
    //   .append("export", `${params.export}`)
    //   .append("thirdparty_customer_id", `${params.thirdparty_customer_id}`)
    //   .append("client_id", `${params.client_id}`)
    //   .append("format", `${params.format}`)
    //   .append("hour_reservation", `${params.hour_reservation}`)
    //   .append('reservation_box', `${params.reservation_box}`);
    const query = new HttpParams({
      fromObject: params,
    });
    return this.http.get(GET_RESERVATION_ORDER_LIST_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 预订单待接受订单数量 */
  getWaitAcceptCount() {
    return this.http.get(
      GET_WAIT_ACCEPT_COUNT_API
    ) as Observable<IHttpResponse>;
  }

  /** 同意订单 */
  approveReservation(params: IDecideOrder) {
    return this.http.post(
      APPROVE_ORDER_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 拒绝订单 */
  declineReservation(params: IDecideOrder) {
    return this.http.post(
      DECLINE_ORDER_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 办理入住和退房、撤销入住撤销退房 */
  handleStayingStatus(params: IHandleStayingStatus) {
    const query = new HttpParams()
      .append('reservation_code', `${params.reservation_code}`)
      .append('reservation_order_code', `${params.reservation_order_code}`)
      .append('to_status', `${params.to_status}`);
    return this.http.get(HANDLE_STAYING_STATUS_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 移除订单标签 */
  removeReservationTag(params: IOperateReservationTag) {
    return this.http.post(
      REMOVE_RESERVATION_TAG_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 今日新增订单 */
  todayAddReservation(params: ITodayReservation) {
    const query = new HttpParams()
      .append('page', `${params.page}`)
      .append('page_size', `${params.page_size}`)
      .append('version', `${params.version}`);
    return this.http.get(TODAY_RESERVATION_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 订单盒子里的订单列表 */
  getReservationBoxList(params: IBoxList) {
    const query = new HttpParams()
      .append('page', `${params.page}`)
      .append('page_size', `${params.page_size}`)
      .append('type', `${params.type}`)
      .append('version', `${params.version}`);
    return this.http.get(BOX_LIST_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  updateReservationTags(params) {
    return this.http.post('/api/reservation/v2/update_tag', params) as Observable<IHttpResponse>
  }

  /** 添加订单标签 */
  addReservationTag(params: IOperateReservationTag) {
    return this.http.post(
      ADD_RESERVATION_TAG_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 获取订单（入住单）列表 */
  getReservations(params: IGetReservationOrderList) {
    const query = new HttpParams().append('code', `${params.code}`);
    return this.http.get(GET_RESERVATIONS_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 获取交易币种 */
  getCurrency() {
    const query = new HttpParams();
    return this.http.get(GET_CURRENCY_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 修改订单入离时间不可选中的日期列表 */
  forbiddenDateList(params: IForbiddenDateList) {
    const query = new HttpParams().append(
      'reservation_code',
      `${params.reservation_code}`
    );
    return this.http.get(FORBIDDEN_DATE_LIST_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** v2.5.1修改订单入住信息 */
  modifyBasicInfo(params: IModifyBasicInfo) {
    return this.http.post(
      MODIFY_BASIC_INFO_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 修改订单金额 */
  setModifyReservationAmount(params: IModifyReservationAmount) {
    return this.http.post(
      MODIFY_RESERVATION_AMOUNT_API,
      params
    ) as Observable<IHttpResponse>;
  }

  lockOrder (params) {
    return this.http.post(LOCK_ORDER_API, params) as Observable<IHttpResponse>;
  }

  setAutomation (params) {
    return this.http.post(SET_AUTOMATION_API, params) as Observable<IHttpResponse>;
  }

  /** 设置订单入离时间 */
  setCheckInAndOutTime(params: ISetCheckinTime) {
    return this.http.post(
      SET_CHECKIN_TIME_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 2.3修改订单备注 */
  addANote(params: IAddANote) {
    return this.http.post(ADD_A_NOTE_API, params) as Observable<IHttpResponse>;
  }

  /** 重新读取订单 */
  refreshReservationOrder(params: IRefreshOrder) {
    return this.http.post(
      REFRESH_RESERVATION_ORDER_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 订单放入订单盒子 */
  putReservationBox(params: IPutReservationBox) {
    return this.http.post(
      PUT_RESERVATION_BOX_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 删除手工订单 */
  deleteHandmadeOrder(params: { reservation_code: string }) {
    return this.http.post(
      DELETE_ORDER_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 获取房态配置 */
  getSettings() {
    const query = new HttpParams();
    return this.http.get(GET_SETTINGS_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 获取订单详情 */
  getOrderDetail(params: IOrderDetail) {
    const query = new HttpParams()
      .append('reservation_code', `${params.reservation_code}`)
      .append('format', `${params.format}`)
      .append('thirdparty_type', `${params.thirdparty_type}`);

    return this.http.get(ORDER_DETAIL_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 订单盒子忽略订单 */
  ignoreReservation(params: IIgnoreReservation) {
    return this.http.post(
      BOX_LIST_IGNORE_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 删除收支页记账 */
  deleteTransaction(params: IDeleteTransaction) {
    return this.http.post(
      DELETE_TRANSACTION_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 更新房客信息*/
  updateGuestInfo(params: IGuestInfo) {
    return this.http.post(
      UPDATE_GUEST_INFO_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 根据订单找可以换的房子列表 */
  getChangeHouseList(params) {
    const query = new HttpParams()
      .append('page', `${params.page}`)
      .append('page_size', `${params.page_size}`)
      .append('reservation_code', `${params.reservation_code}`)
      .append('house_name', `${params.house_name}`);
    return this.http.get(CHANGE_HOUSE_LIST_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 订单换房到具体哪个房源 */
  setChangeToHouse(params: IChangeToHouse) {
    return this.http.post(
      CHANGE_TO_HOUSE_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 订单续住 */
  expand(params: IExpand) {
    return this.http.post(EXPAND_API, params) as Observable<IHttpResponse>;
  }

  /** 直连失败房源详情 */
  getAirbnbFailedDetail(params: IAirbnbFailedDetail) {
    const query = new HttpParams().append(
      'origin_house_id',
      `${params.origin_house_id}`
    );
    return this.http.get(AIRBNB_FAILED_DETAIL_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 提交审核 */
  airbnbRetry(params: IAirbnbRetry) {
    return this.http.post(
      AIRBNB_RETRY_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 审核失败列表 */
  getAirbnbFailedList() {
    const query = new HttpParams();
    return this.http.get(AIRBNB_FAILED_LIST_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 添加手工订单 */
  bookByHand(params) {
    return this.http.post(
      BOOK_BY_HAND_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /**
   * 获取房源列表
   * @param params HouseListParamsInterface
   * @return Observable<HouseListResultInterface>
   */
  getHouseList(params: HouseListParamsInterface) {
    const query = new HttpParams()
      .append('page', `${params.page || 1}`)
      .append('page_size', `${params.page_size}`)
      .append('title', `${params.title}`)
      .append('group_id', `${params.group_id}`)
      .append('house_type_id', `${params.house_type_id}`)
      .append('operator_relation_id', `${params.operator_relation_id}`);

    return this.http.get(HOUSE_LIST_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 订单页面设置入住指南信息 */
  setGuide(params: ISetGuideInterface) {
    return this.http.post(SET_GUIDE_API, params) as Observable<IHttpResponse>;
  }

  /** 获取入住单入住指南分享文案 */
  getShareUrl(params: IGetShareInfo) {
    const query = new HttpParams().append(
      'reservation_code',
      `${params.reservation_code}`
    );
    return this.http.get(GET_SHARE_URL_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  /** 给用户发送入住指南邮件 */
  sendGuideEmail(params: ISendGuideEmail) {
    return this.http.post(
      SEND_GUIDE_EMAIL_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 编辑审核状态 */
  ediGuideAuditingStatus(params: IEditGuideAuditingStatus) {
    return this.http.post(
      EDIT_GUIDE_AUDITING_STATUS_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 入住凭证图片关联到房客 */
  relateCredentialToGuest(params: IRelateCredentialToGuest) {
    return this.http.post(
      RELATE_CREDENTIAL_TO_GUEST_API,
      params
    ) as Observable<IHttpResponse>;
  }

  /** 添加审核照片 */
  addGuideImage(params: IAddGuideImage) {
    return this.http.post(
      ADD_GUIDE_IMAGE_API,
      params
    ) as Observable<IHttpResponse>;
  }

  batchPutBox (params) {
    return this.http.post(BATCH_PUT_BOX_API, params) as Observable<IHttpResponse>;
  }

  batchChangeHouse (params) {
    return this.http.post(BATCH_CHANGE_HOUSE_API, params) as Observable<IHttpResponse>;
  }

  fetchSubaccountList (params) {
    const query = new HttpParams({
      fromObject: params
    });
    return this.http.get(SUBACCOUNT_LIST_API, { params: query }) as Observable<IHttpResponse>;
  }

  requestSetCreator(params) {
    return this.http.post(SET_CREATOR_API, params) as Observable<IHttpResponse>;
  }
}
